import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Layout } from '@community_dev/pixels'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Collapse,
  Divider,
  Empty,
  Form,
  Input,
  notification,
  Popconfirm,
  Space,
  Tooltip,
  Typography,
} from 'antd'
import type { JSX } from 'react'

import { StatusTag } from './StatusTag'

import { associateCampaign, ProvisioningCampaign, removeCampaign } from 'api/infobipProvisioning'
import { QUERIES } from 'constants/queries'

const { Panel } = Collapse

type CampaignCardProps = {
  campaign: ProvisioningCampaign
  canWrite: boolean
  clientId: string
  disabledTooltip: string
}

const CampaignCard = ({ campaign, canWrite, clientId, disabledTooltip }: CampaignCardProps): JSX.Element => {
  const queryClient = useQueryClient()

  const { mutate: removeCampaignMutation, isLoading: isRemoving } = useMutation({
    mutationFn: removeCampaign,
    onSuccess: () => {
      notification.success({
        message: 'Campaign removed successfully',
        placement: 'bottomRight',
        duration: 2,
      })
      queryClient.invalidateQueries([QUERIES.PROVISIONING_STATUS, clientId])
    },
    onError: () => {
      notification.error({
        message: 'Failed to remove campaign',
        placement: 'bottomRight',
        duration: 2,
      })
    },
  })

  return (
    <Card
      extra={
        <Space>
          <StatusTag status={campaign.status} />
          <Popconfirm
            cancelText="Cancel"
            disabled={!canWrite}
            okText="Remove"
            onConfirm={() => removeCampaignMutation({ clientId, campaignId: campaign.campaignId })}
            title="Are you sure you want to remove this campaign?"
          >
            <Tooltip title={!canWrite ? disabledTooltip : ''}>
              <span>
                <Button danger disabled={!canWrite} icon={<DeleteOutlined />} loading={isRemoving} size="small">
                  Remove
                </Button>
              </span>
            </Tooltip>
          </Popconfirm>
        </Space>
      }
      size="small"
      style={{ marginBottom: '12px' }}
      title={campaign.campaignId}
    >
      <Collapse ghost>
        <Panel header="Status Checks" key="checks">
          {campaign.checks.map((check) => (
            <Layout key={check.id} marginBottom="12px">
              <Layout alignItems="center" display="flex" gap="8px" marginBottom="4px">
                <Typography.Text strong>{check.label}</Typography.Text>
                <StatusTag status={check.status} />
              </Layout>
              {check.help.length > 0 && (
                <Typography.Text type="secondary">
                  {check.help.map((helpText, index) => (
                    <div key={index}>{helpText}</div>
                  ))}
                </Typography.Text>
              )}
            </Layout>
          ))}
        </Panel>
      </Collapse>

      {campaign.linkedToResources.length > 0 && (
        <Layout marginTop="8px">
          <Typography.Text type="secondary">Linked to: {campaign.linkedToResources.join(', ')}</Typography.Text>
        </Layout>
      )}
    </Card>
  )
}

type TCRCampaignsProps = {
  campaigns: ProvisioningCampaign[]
  canWrite: boolean
  clientId: string
  disabledTooltip: string
}

export const TCRCampaigns = ({ clientId, campaigns, canWrite, disabledTooltip }: TCRCampaignsProps): JSX.Element => {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()

  const { mutate: addCampaign, isLoading: isAddingCampaign } = useMutation({
    mutationFn: associateCampaign,
    onSuccess: () => {
      notification.success({
        message: 'Campaign associated successfully',
        placement: 'bottomRight',
        duration: 2,
      })
      form.resetFields()
      queryClient.invalidateQueries([QUERIES.PROVISIONING_STATUS, clientId])
    },
    onError: () => {
      notification.error({
        message: 'Failed to associate campaign',
        placement: 'bottomRight',
        duration: 2,
      })
    },
  })

  const handleAddCampaign = (values: { campaignId: string }) => {
    if (!canWrite) return
    addCampaign({ clientId, campaignId: values.campaignId.trim() })
  }

  return (
    <Card title="TCR Campaigns">
      <Form form={form} layout="inline" onFinish={handleAddCampaign} style={{ marginBottom: '16px' }}>
        <Form.Item name="campaignId" rules={[{ required: true, message: 'Please enter a campaign ID' }]}>
          {canWrite ? (
            <Input placeholder="Enter TCR Campaign ID (e.g., CAM123)" style={{ width: 280 }} />
          ) : (
            <Tooltip title={disabledTooltip}>
              <span>
                <Input disabled placeholder="Enter TCR Campaign ID (e.g., CAM123)" style={{ width: 280 }} />
              </span>
            </Tooltip>
          )}
        </Form.Item>
        <Form.Item>
          {canWrite ? (
            <Button htmlType="submit" icon={<PlusOutlined />} loading={isAddingCampaign} type="primary">
              Add Campaign
            </Button>
          ) : (
            <Tooltip title={disabledTooltip}>
              <span>
                <Button disabled htmlType="submit" icon={<PlusOutlined />} loading={isAddingCampaign} type="primary">
                  Add Campaign
                </Button>
              </span>
            </Tooltip>
          )}
        </Form.Item>
      </Form>
      <Typography.Text type="secondary">
        {
          "Use TCR's dashboard for campaign submission. It is critical that the connectivity partner is set to Open Market / Infobip."
        }
      </Typography.Text>
      <Divider />
      {campaigns.length === 0 ? (
        <Empty description="No campaigns associated yet" />
      ) : (
        campaigns.map((campaign) => (
          <CampaignCard
            campaign={campaign}
            canWrite={canWrite}
            clientId={clientId}
            disabledTooltip={disabledTooltip}
            key={campaign.campaignId}
          />
        ))
      )}
    </Card>
  )
}
